import { Recipe, RecipeID } from '@shared/models/recipe.model';
import { devtools } from 'zustand/middleware';
import { toLookup } from '@shared/helpers/utils.helpers';
import axios, { AxiosRequestConfig } from 'axios';
import { IRecipeStoreState } from './recipe-state.model';
import { updateResourceLocally } from '../store.helpers';
import { UpdateClientAction } from '../store.model';
import { initHmrStore, getHmrStoreState } from '../../helpers/vite.helpers';
import { createWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

const STORE_NAME = 'recipes';

const requestConfig: AxiosRequestConfig = {
  transformResponse: (data: any): any => {
    if ((data as string).startsWith('<')) {
      return;
    }
    return JSON.parse(data) as Recipe[];
  },
};

/**
 * Store for recipes. Avoid using this store directly, instead use the hooks in recipe.hooks.ts
 */
export const useRecipesStore = createWithEqualityFn<IRecipeStoreState>()(
  devtools(
    (set, get) => ({
      recipes: [],
      recipesLookup: {},
      fetching: false,
      fetched: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
      ...getHmrStoreState(STORE_NAME),

      fetchRecipes: async (organization: string) => {
        set(() => ({ fetching: true, fetched: false, error: undefined }));
        try {
          const { data: recipes } = await axios.get<Recipe[]>(`recipes`, {
            headers: { Organization: organization },
            ...requestConfig,
          });

          set(() => ({
            recipes,
            recipesLookup: toLookup(recipes),
            fetching: false,
            fetched: true,
          }));
        } catch (err: unknown) {
          set(() => ({
            fetching: false,
            fetched: false,
            error: err instanceof Error ? err : new Error(String(err)),
          }));
          return await Promise.reject(err);
        }
      },

      createRecipe: async (recipe) => {
        const { updateRecipesLocally } = get();

        set(() => ({ isCreating: true }));

        try {
          const { data } = await axios.post<Recipe>(
            `recipes`,
            recipe,
            requestConfig,
          );
          updateRecipesLocally({
            action: UpdateClientAction.Add,
            itemOrId: data,
          });
          return data;
        } catch (err: unknown) {
          set(() => ({ isCreating: false }));
          return await Promise.reject(err);
        }
      },

      cloneRecipe: async (recipe, destinationOrganization, overwrite) => {
        try {
          const { data } = await axios.put<Recipe[]>(`recipes/clone`, recipe, {
            ...requestConfig,
            params: { overwrite, destinationOrganization },
          });
          return data;
        } catch (err: unknown) {
          return await Promise.reject(err);
        }
      },

      updateRecipe: async (recipe) => {
        const { updateRecipesLocally } = get();

        set(() => ({ isUpdating: true }));

        try {
          const { data } = await axios.put<Recipe>(
            `/recipes`,
            recipe,
            requestConfig,
          );

          updateRecipesLocally({
            action: UpdateClientAction.Update,
            itemOrId: data,
          });

          return data;
        } catch (err: unknown) {
          set(() => ({ isUpdating: false }));
          return await Promise.reject(err);
        }
      },

      deleteRecipe: async (id: RecipeID) => {
        const { updateRecipesLocally } = get();

        set(() => ({ isDeleting: true }));

        try {
          const { data } = await axios.delete<Recipe>(
            `/recipes/${id}`,
            requestConfig,
          );
          // Soft-delete: keep the recipe in the store (now carrying deleted_at)
          // so element/property references still resolve. Pickers hide it.
          updateRecipesLocally({
            action: UpdateClientAction.Update,
            itemOrId: data,
          });
          return data;
        } catch (err: unknown) {
          set(() => ({ isDeleting: false }));
          return await Promise.reject(err);
        }
      },

      updateRecipesLocally: ({
        action,
        itemOrId,
        options: {
          isCreating = false,
          isUpdating = false,
          isDeleting = false,
        } = {},
      }) => {
        let { recipes, recipesLookup: lookup } = get();

        if (
          action === UpdateClientAction.Add ||
          action === UpdateClientAction.Update
        ) {
          const { collection: updatedRecipes, lookup: updatedLookup } =
            updateResourceLocally({
              action,
              itemOrId,
              collection: recipes,
            });

          recipes = updatedRecipes ?? recipes;
          lookup = updatedLookup ?? lookup;
        }

        if (action === UpdateClientAction.Remove) {
          // Explicit type argument: T is not inferable from the Remove branch
          const { collection: updatedRecipes, lookup: updatedLookup } =
            updateResourceLocally<Recipe>({
              action,
              itemOrId,
              collection: recipes,
            });

          recipes = updatedRecipes ?? recipes;
          lookup = updatedLookup ?? lookup;
        }

        set(() => ({
          isCreating,
          isUpdating,
          isDeleting,
          recipes,
          recipesLookup: lookup,
        }));
      },
    }),
    { name: STORE_NAME },
  ),
  shallow,
);

initHmrStore(STORE_NAME, useRecipesStore);
